import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useBlockSlot, useContentToken, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, SectionContextProvider } from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faXTwitter,
  faInstagram,
  faTiktok,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const twitterLinkDef: SlotGroupDefinition = {
  label: 'Twitter / X',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_twitter_url',
      suggestedFields: ['property_twitter_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Twitter' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_tiktok_url',
      suggestedFields: ['property_tiktok_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'TikTok' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const googleLinkDef: SlotGroupDefinition = {
  label: 'Google Business',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Google' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const linkClasses =
  'group flex flex-col items-center gap-3 p-6 transition-all duration-300';
const iconWrapClasses =
  'w-14 h-14 rounded-full border-2 border-[var(--vibe-color-primary)] flex items-center justify-center text-[var(--vibe-color-primary)] social-icon-wrap transition-all duration-300';
const labelClasses =
  'font-body text-sm uppercase tracking-wider text-[var(--vibe-color-text-muted)] group-hover:text-[var(--vibe-color-primary)] transition-colors duration-300';

export function SocialFollow() {
  const title = useContentToken('socialfollow.title');
  const subtitle = useContentToken('socialfollow.subtitle');

  const facebook = useSlotGroup('socialfollow.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('socialfollow.instagram_link', instagramLinkDef);
  const twitter = useSlotGroup('socialfollow.twitter_link', twitterLinkDef);
  const tiktok = useSlotGroup('socialfollow.tiktok_link', tiktokLinkDef);
  const google = useSlotGroup('socialfollow.google_link', googleLinkDef);

  const instagramEmbedSlot = useBlockSlot('socialfollow.instagram_embed_block', {
    defaultValue: 'instagram-oembed',
  });

  const hasAnyIcon =
    facebook.url ||
    instagram.url ||
    twitter.url ||
    tiktok.url ||
    google.url;
  const hasAnySocial = hasAnyIcon;

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const iconsRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div ref={headingRef} className="reveal-fade-up">
          <EditableToken
            path="socialfollow.title"
            as="h2"
            className="font-heading text-3xl sm:text-4xl font-normal text-[var(--vibe-color-text)] mb-4 uppercase tracking-wide social-title-gradient"
          >
            {title}
          </EditableToken>

          <div className="w-16 h-1 mx-auto mb-4 rounded-full social-accent-bar" />

          <EditableToken
            path="socialfollow.subtitle"
            as="p"
            className="font-body text-lg text-[var(--vibe-color-text-muted)] max-w-2xl mx-auto mb-10"
          >
            {subtitle}
          </EditableToken>
        </div>

        {hasAnyIcon ? (
          <div ref={iconsRef} className="reveal-fade-up flex flex-wrap justify-center gap-4 md:gap-6">
            {facebook.url && (
              <EditableSlotGroup groupPath="socialfollow.facebook_link" definition={facebookLinkDef}>
                <a href={facebook.url} target={facebook._target || '_blank'} rel="noopener noreferrer" aria-label="Facebook" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faFacebook} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{facebook.label || 'Facebook'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {twitter.url && (
              <EditableSlotGroup groupPath="socialfollow.twitter_link" definition={twitterLinkDef}>
                <a href={twitter.url} target={twitter._target || '_blank'} rel="noopener noreferrer" aria-label="Twitter" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faXTwitter} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{twitter.label || 'Twitter'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {instagram.url && (
              <EditableSlotGroup groupPath="socialfollow.instagram_link" definition={instagramLinkDef}>
                <a href={instagram.url} target={instagram._target || '_blank'} rel="noopener noreferrer" aria-label="Instagram" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faInstagram} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{instagram.label || 'Instagram'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {tiktok.url && (
              <EditableSlotGroup groupPath="socialfollow.tiktok_link" definition={tiktokLinkDef}>
                <a href={tiktok.url} target={tiktok._target || '_blank'} rel="noopener noreferrer" aria-label="TikTok" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faTiktok} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{tiktok.label || 'TikTok'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {google.url && (
              <EditableSlotGroup groupPath="socialfollow.google_link" definition={googleLinkDef}>
                <a href={google.url} target={google._target || '_blank'} rel="noopener noreferrer" aria-label="Google" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faGoogle} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{google.label || 'Google'}</span>
                </a>
              </EditableSlotGroup>
            )}
          </div>
        ) : !hasAnySocial ? (
          <p className="font-body text-[var(--vibe-color-text-muted)]">
            Social media links will appear here.
          </p>
        ) : null}

        {instagram.url && instagramEmbedSlot && (
          <EditableToken path="socialfollow.instagram_embed_block" as="div" className="social-ig-embed">
            <SectionContextProvider sectionId={instagramEmbedSlot.sectionId}>
              <instagramEmbedSlot.BlockComponent sectionId={instagramEmbedSlot.sectionId} />
            </SectionContextProvider>
          </EditableToken>
        )}
      </div>

      <style>{`
        .social-title-gradient {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .social-accent-bar {
          background: linear-gradient(90deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 75%, black) 100%);
        }
        .group:hover .social-icon-wrap {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-color: transparent;
          color: white;
        }
        .social-ig-embed {
          margin-top: 3rem;
          width: 100%;
          max-width: 420px;
          margin-left: auto;
          margin-right: auto;
        }
        @media (min-width: 768px) {
          .social-ig-embed {
            margin-top: 3.5rem;
            max-width: 480px;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SocialFollow',
  displayName: 'Social Follow',
  tokens: {
    'socialfollow.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Follow Us',
    },
    'socialfollow.subtitle': {
      label: 'Subtitle',
      type: 'text',
      defaultValue: 'Stay connected with our community on social media.',
    },
    'socialfollow.instagram_embed_block': {
      label: 'Instagram Embed Block',
      type: 'block',
      defaultValue: 'instagram-oembed',
      acceptedBlockCategories: ['Social'],
    },
  },
  slotGroups: {
    'socialfollow.facebook_link': facebookLinkDef,
    'socialfollow.instagram_link': instagramLinkDef,
    'socialfollow.twitter_link': twitterLinkDef,
    'socialfollow.tiktok_link': tiktokLinkDef,
    'socialfollow.google_link': googleLinkDef,
  },
};

(SocialFollow as any).contentSchema = contentSchema;

export default SocialFollow;
